import * as React from 'react';
import { Card, Button, Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { fetchApi, GCacheRecord, makeResetCache, notify } from 'gossamer-front-end';
import { EventExtendedViewRow, TicketItemWithBookings } from 'cb-model';
import { EventPaymentCheckoutModal } from './EventPaymentCheckoutModal';

interface EventBookingCardProps {
  eventRecord: GCacheRecord<EventExtendedViewRow>;
}

export const EventBookingCard = (props: EventBookingCardProps): React.JSX.Element => {
  const dispatch = useDispatch();

  const resetCache = makeResetCache(dispatch);
  const [selectedTicketId, setSelectedTicketId] = React.useState<number | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = React.useState(false);
  const tickets = (props.eventRecord.data?.ticket_data || []) as TicketItemWithBookings[];
  const eventIsPast = props.eventRecord.data?.end_at && new Date(props.eventRecord.data.end_at) < new Date();
  const selectedTicket = tickets.find((ticket) => ticket.id === selectedTicketId);

  React.useEffect(() => {
    if (selectedTicketId === null && tickets.length === 1 && tickets[0].quantity - tickets[0].bookings > 0) {
      setSelectedTicketId(tickets[0].id);
    }
  }, [selectedTicketId, tickets]);

  const handleBook = async () => {
    if (!selectedTicket || selectedTicket.quantity - selectedTicket.bookings <= 0) {
      notify(['The selected ticket is no longer available.', 'danger']);
      return;
    }

    if (Number(selectedTicket.price || 0) > 0) {
      setPaymentModalOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      await fetchApi('POST', '/main/book-user-with-ticket', { ticketId: selectedTicketId });
      resetCache('BookingView');
      notify(['Booking complete', 'success']);
    } catch (err: any) {
      notify([err?.message || 'Booking failed', 'danger']);
    } finally {
      setSubmitting(false);
    }
  };

  const renderTickets = () => (
    <div className="mb-3">
      {tickets.map((ticket) => (
        <TicketOption
          key={ticket.id}
          ticket={ticket}
          onSelect={(ticketId) => setSelectedTicketId(ticketId)}
          selectedTicketId={selectedTicketId}
        />
      ))}
    </div>
  );

  const selectedTicketAvailable = selectedTicket ? selectedTicket.quantity - selectedTicket.bookings > 0 : false;
  const renderBookButton = () => (
    <Button size="sm" onClick={handleBook} disabled={submitting || !selectedTicketId || !selectedTicketAvailable}>
      {submitting ? 'Booking...' : 'Book event'}
    </Button>
  );

  return (
    <>
      <Card>
        <Card.Header>
          Booking status:&nbsp;<strong>Not booked</strong>
        </Card.Header>
        <Card.Body>
          {eventIsPast && <p className="text-muted">This event has already taken place.</p>}
          {!eventIsPast && renderTickets()}
          {!eventIsPast && renderBookButton()}
        </Card.Body>
      </Card>
      {!!props.eventRecord.data?.id && selectedTicketId !== null && (
        <EventPaymentCheckoutModal
          open={paymentModalOpen}
          onOpenChange={setPaymentModalOpen}
          eventId={props.eventRecord.data.id}
          ticketId={selectedTicketId}
        />
      )}
    </>
  );
};

const TicketOption = (props: {
  ticket: TicketItemWithBookings;
  onSelect: (ticketId: number) => void;
  selectedTicketId: number | null;
}) => {
  const available = props.ticket.quantity - props.ticket.bookings;
  const availableStr = available > 0 ? `${available} available` : 'Sold out';
  const priceStr = props.ticket.price > 0 ? `£${props.ticket.price.toFixed(2)}` : 'Free';
  return (
    <Form.Check id={`option-${props.ticket.id}`}>
      <Form.Check.Input
        type="radio"
        name="ticketOption"
        onChange={() => props.onSelect(props.ticket.id || 0)}
        disabled={available <= 0}
        checked={props.ticket.id === props.selectedTicketId}
        isValid={props.ticket.id === props.selectedTicketId}
        value={props.ticket.id}
      />
      <Form.Check.Label>
        <b>{props.ticket.title}</b> - {priceStr} - <span className="text-muted">{availableStr}</span>
      </Form.Check.Label>
    </Form.Check>
  );
};
